/**
 * Global runtime error boundary (APP-001 §12, §33).
 *
 * Catches unexpected render-time errors anywhere in the authenticated tree and
 * shows a safe ErrorState (no raw stack traces, no secrets). The boundary
 * never swallows errors silently; it logs only a redacted summary.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from '@/components/feedback/ErrorState';

interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log a redacted summary only — never the full stack with user data.
    // In production, an error-reporting provider (if selected) would scrub
    // Authorization/Clerk tokens/API keys before sending (APP-001 §24).
    console.error('[AppErrorBoundary]', error.name, error.message, {
      componentStack: info.componentStack?.slice(0, 200),
    });
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center p-6">
          <ErrorState
            title="Something went wrong"
            description="An unexpected error occurred. Please reload the page."
            error={this.state.error}
            retryLabel="Reload"
            onRetry={() => window.location.reload()}
          />
        </div>
      );
    }
    return this.props.children;
  }
}
